/**
 * A thing's name, and a list, in the language on screen (CLAUDE.md §8).
 *
 * Reference data carries a name per language — `name_ar`, `name_en`, `name_fr` — and a screen that
 * prints `nameAr` whatever the reader chose shows Arabic on an English page. That is how the Service
 * classes card came to read `بين المدن` beside `cross_cluster` with EN selected (2026-09-28). So every
 * place that shows one name asks this function; only an edit form, which has a field per language,
 * shows them side by side.
 *
 * The fallback keeps Arabic as the floor, as §8 does: French falls back to English, then Arabic;
 * English to Arabic; Arabic to English, then French, so a thing that only has an English name still
 * shows one. A blank counts as missing — `''` is what an empty form field stores. Merchants, Couriers
 * and Hubs have no French column, so on a French screen they show their English name.
 *
 * Four private copies of these existed before (`goodsCategoryName`, the tariff editor's `nameOf`, and
 * two `listOf`s); a fifth screen would have written a sixth.
 */
import type { Locale } from './index.js';

export interface LocalizedNames {
  readonly ar?: string | null | undefined;
  readonly en?: string | null | undefined;
  readonly fr?: string | null | undefined;
}

const ORDER: Readonly<Record<Locale, readonly (keyof LocalizedNames)[]>> = {
  ar: ['ar', 'en', 'fr'],
  en: ['en', 'ar', 'fr'],
  fr: ['fr', 'en', 'ar'],
};

function present(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/** The name in `locale`, falling back as above; `'—'` when there is none at all. */
export function localName(names: LocalizedNames, locale: string): string {
  const order = ORDER[(locale in ORDER ? locale : 'ar') as Locale];
  for (const key of order) {
    const value = names[key];
    if (present(value)) return value;
  }
  return '—';
}

/** The common row shape: `nameAr` required, the others optional. */
export function rowName(
  row: { readonly nameAr: string; readonly nameEn?: string | null; readonly nameFr?: string | null },
  locale: string,
): string {
  return localName({ ar: row.nameAr, en: row.nameEn, fr: row.nameFr }, locale);
}

/** One part of a list in the reader's language: an item, or the words and commas between items. */
export interface ListPart {
  readonly type: 'element' | 'literal';
  readonly value: string;
}

/**
 * A list's parts in the reader's language, so each item can be drawn in its own element — an
 * identifier in its own left-to-right isolate inside an Arabic sentence (SC2c). `listOf` is for items
 * that are words and may run together; this is for `OFAC-SDN@…` and the like, which may not. `'—'`
 * when empty.
 */
export function listParts(items: readonly string[], locale: string): readonly ListPart[] {
  if (items.length === 0) return [{ type: 'literal', value: '—' }];
  return new Intl.ListFormat(locale, { style: 'short', type: 'unit' })
    .formatToParts(items)
    .map((part) => ({ type: part.type, value: part.value }));
}

/** A list in the reader's own language — "a, b and c", "a، b و c". `'—'` when empty. */
export function listOf(items: readonly string[], locale: string): string {
  return items.length === 0 ? '—' : new Intl.ListFormat(locale, { style: 'short', type: 'unit' }).format(items);
}
